import React, { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import {
  Route,
  Redirect,
  useHistory,
  useRouteMatch,
  useLocation,
  useParams,
} from 'react-router-dom';
import { Grid } from "@mui/material";
import { Telegram as TelegramIcon } from "@mui/icons-material";
import { useMeasure } from 'react-use';
import { AppBar, Page } from 'components';
import {
  CHAT,
  CREATE_ACT_REVISION,
  CREATE_CLAIM,
  CREATE_PAYMENT_SCHEDULE,
} from 'constants/actions';
import { getLastUrlSegment } from 'utils/string';
import { useTranslation } from 'hooks';
import { useDebtorInfo } from 'hooks/api/cascade';
import { CascadeTabs, CascadePanels } from './components';
import { tabs, actions as restActions } from './CascadeScreen.mock';

const getInitialTab = (pathname) => {
  const lastUrlSegment = getLastUrlSegment(pathname);
  const hasPredefinedTab = tabs.some((tab) => tab.value === lastUrlSegment);

  return hasPredefinedTab ? lastUrlSegment : tabs[0].value;
};

const CascadeScreen = () => {
  const [panelRef, { height: listHeight }] = useMeasure();
  const { url, path } = useRouteMatch();
  const history = useHistory();
  const { pathname } = useLocation();
  const [initialTab] = useState(() => getInitialTab(pathname));
  const { t } = useTranslation();
  const { entityId } = useParams();
  const {
    isLoading,
    isError,
    isSuccess,
    error,
    data: debtorInfo,
  } = useDebtorInfo(entityId);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (isError) {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  }, [isError]); // eslint-disable-line react-hooks/exhaustive-deps

  const unreadChatMessages = isSuccess ? debtorInfo.unreadChatMessages : 0;
  const title = isSuccess
    ? `(${debtorInfo.debitor.id}) ${debtorInfo.debitor.name}`
    : '';
  const actions = [
    {
      id: CHAT,
      title: t(CHAT),
      icon: TelegramIcon,
      show: 'always',
      badge: { badgeContent: unreadChatMessages, color: 'primary' },
    },
    ...restActions,
  ];

  const handleBackClick = () => {
    history.goBack();
  };

  const handleActionItemSelected = (item) => {
    switch (item) {
      case CHAT:
        history.replace(`${CHAT}`, { from: CHAT });
        break;
      case CREATE_ACT_REVISION:
        history.replace(`${url}/revisionAct`);
        break;
      case CREATE_CLAIM:
        history.replace(`${url}/claim`);
        break;
      case CREATE_PAYMENT_SCHEDULE:
        history.replace(`${url}/newInvoice`);
        break;
      default:
        break;
    }
  };

  return (
    <Page fullScreen>
      <Grid container direction="column" sx={{ height: '100%', minHeight: 0, width: '100%', margin: 0 }}>
        <AppBar
          title={title}
          loading={isLoading}
          actions={actions}
          onActionSelected={handleActionItemSelected}
          navButtonAriaLabel={t('openSideMenu')}
          onNavClick={handleBackClick}
          navIcon="back"
        />

        <Grid item xs sx={{ minHeight: 0, display: 'flex', flexDirection: 'column', width: '100%' }}>
          <Route path={`${path}/:tabId`}>
            <CascadeTabs tabs={tabs} />
            <div ref={panelRef} style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              <CascadePanels tabs={tabs} listHeight={listHeight} />
            </div>
          </Route>
        </Grid>

        <Redirect from={url} to={`${url}/${initialTab}`} />
      </Grid>
    </Page>
  );
};

export default CascadeScreen;
